import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import type { FerryDetailsModalData } from '@apps/main/new-desktop/modules/ferryDetailsModal';
import {
  useBookTripStore,
  useBrowserStore,
  useEditingIngredientStore,
  usePaxStore,
  useStatsigEventsStore,
  useUserStore,
} from '@apps/main/new-desktop/core/stores';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import type { HotelFilterUpdate } from '@apps/main/new-desktop/modules/hotelFilters';
import type { EditTripStep } from '@apps/main/new-desktop/modules/editTripModal';
import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import {
  CoreEventSource,
  EditTripModalOpenedLocationType,
  RemoveHotelSelectionFiltersOrigin,
} from '@apps/main/new-desktop/core/models';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { useUserLocationStore } from '@apps/main/new-desktop/core/stores/userLocation';
import type { FlightDetailsModalData } from '@apps/main/new-desktop/modules/flightDetailsModal';
import type { FerryVehiclesRouteData } from '@apps/main/new-desktop/modules/ferryMultipleVehiclesModal';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useDestinationSelectorStore } from '@apps/main/new-desktop/core/stores/destinationSelectorStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';
import type { ConfirmationWarningMessage } from '@apps/main/new-desktop/modules/ariportDistanceWarningModal';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type {
  AutoCompleteSuggestion,
  IngredientId,
  Pax,
  PortInfo,
} from '@libs/trip-planner-interfaces';
import {
  isLocationSuggestion,
  TransportType,
} from '@libs/trip-planner-interfaces';
import { isStena } from '@libs/cookie';
import { LocalStorageService } from '@libs/utils';
import { blockBackgroundScroll, enableBackgroundScroll } from '@libs/ui';

import {
  ConfigurationPanelQueryParamValue,
  ExternalErrorQueryParamValue,
} from '../../../core/models/navigation';
import { ItinerarySegmentType, TripOverviewModalType } from '../models';
import type {
  ItineraryNoTransportSegment,
  PortSelectionUpdatePayload,
} from '../models';
import { useQueryParamsStore } from '../../../core/stores/queryParamsStore';

import { usePortSelectorStore } from './portSelector/portSelectorStore';
import { useTripOverviewPageStore } from './tripOverviewPageStore';
import { useTripOverviewFerryMultipleVehiclesStore } from './tripOverviewFerryMultipleVehiclesStore';
import { useTripOverviewPanelsStore } from './tripOverviewPanelsStore';

export const useTripOverviewModalsStore = defineStore(
  'TripOverviewModalsStore',
  () => {
    const tripOverviewFerryMultipleVehiclesStore =
      useTripOverviewFerryMultipleVehiclesStore();
    const portSelectorStore = usePortSelectorStore();
    const recipeStore = useRecipeStore();
    const queryParamsStore = useQueryParamsStore();
    const destinationSelectorStore = useDestinationSelectorStore();
    const tripOverviewPageStore = useTripOverviewPageStore();
    const editTripStore = useEditTripStore();
    const statsigEventsStore = useStatsigEventsStore();
    const userLocationStore = useUserLocationStore();
    const bookTripStore = useBookTripStore();
    const editingIngredientStore = useEditingIngredientStore();
    const tripOverviewPanelsStore = useTripOverviewPanelsStore();
    const browserStore = useBrowserStore();
    const paxStore = usePaxStore();
    const filtersStore = useFiltersStore();
    const ferryFiltersStore = useFerryFiltersStore();
    const userStore = useUserStore();
    const { t } = i18n.global;

    const localStorageService = new LocalStorageService();

    // State
    const isCookieModalOpen = ref(true);
    const tripOverviewModalType = ref<TripOverviewModalType>(
      TripOverviewModalType.None
    );
    const removeDestinationModalFor = ref<IngredientId | undefined>(undefined);
    const ferryDetailsModalData = ref<FerryDetailsModalData | undefined>();
    const flightDetailsModalData = ref<FlightDetailsModalData | undefined>();
    const hasAlreadyOpenedInitialFerryModal = ref<boolean>(false);
    const amountReloadPageModalOpen = ref<number>(0);
    const accommodationUpsellModalOpenCounter = ref<number>(0);
    const confirmationWarningData = ref<ConfirmationWarningMessage[]>([]);

    // Watchers

    watch(
      () => tripOverviewModalType,
      () => {
        if (browserStore.isSmallerThan900px) {
          if (tripOverviewModalType.value === TripOverviewModalType.None) {
            enableBackgroundScroll();
          } else {
            blockBackgroundScroll();
          }
        }
      },
      { immediate: true }
    );

    watch(
      () => bookTripStore.bookingError,
      () => {
        if (bookTripStore.bookingError) {
          tripOverviewModalType.value = TripOverviewModalType.BookingError;
        } else {
          closeTripOverviewModal();
        }
      }
    );

    // Getters
    const isAnyModalOpen = computed<boolean>(
      () =>
        isCookieModalOpen.value ||
        tripOverviewModalType.value !== TripOverviewModalType.None
    );

    // Actions
    function openInitialFerryModalWhenPossible(): void {
      if (shouldOpenInitialFerryModal()) {
        openInitialFerryModal();
        hasAlreadyOpenedInitialFerryModal.value = true;
      }

      function shouldOpenInitialFerryModal(): boolean {
        const hotelOnlyLength = recipeStore.hotelOnlyRecipe.length;
        const recipeLength = recipeStore.recipe.length;
        const hasExactlyOneHotelIngredient =
          recipeLength === 1 && hotelOnlyLength === 1;

        return (
          !hasAlreadyOpenedInitialFerryModal.value &&
          hasExactlyOneHotelIngredient &&
          isStena()
        );
      }

      function openInitialFerryModal(): void {
        const ingredient = recipeStore.hotelOnlyRecipe[0];

        if (ingredient) {
          const fakeSegment: ItineraryNoTransportSegment = {
            segmentType: ItinerarySegmentType.NoTransport,
            ingredientId: ingredient.ingredientId,
            isReturnTransport: true,
            isLastSegment: false,
            city: undefined,
          };
          tripOverviewPageStore.selectTransport(
            fakeSegment,
            TransportType.Ferry
          );
        }
      }
    }

    function openExternalErrorModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.ExternalError;
    }

    function closeTripOverviewModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openTripPriceSummaryModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.TripPriceSummary;
    }

    function closeTripPriceSummaryModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openFerryDetailsModal(modalData: FerryDetailsModalData): void {
      ferryDetailsModalData.value = modalData;
      tripOverviewModalType.value = TripOverviewModalType.FerryDetails;
    }

    function closeFerryDetailsModal(): void {
      ferryDetailsModalData.value = undefined;
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openTripCalendarModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.TripCalendar;
    }

    function closeTripCalendarModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openWhyWeSuggestModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.WhyWeSuggest;
    }

    function openEditTripModal(
      initialStep: EditTripStep = { stepType: EditTripStepType.Overview }
    ): void {
      const editTripStepTypeToStatsigEvent: Record<
        EditTripStepType,
        EditTripModalOpenedLocationType
      > = {
        [EditTripStepType.AddDestination]:
          EditTripModalOpenedLocationType.TripOverviewAddDestinationButton,
        [EditTripStepType.UpdateDestination]:
          EditTripModalOpenedLocationType.TripOverviewDestinationSegment,
        [EditTripStepType.Overview]:
          EditTripModalOpenedLocationType.TripOverviewEditTripButton,
        [EditTripStepType.AddDestinationToOwnStay]:
          EditTripModalOpenedLocationType.TripOverviewAddDestinationToOwnStayButton,
      };
      statsigEventsStore.logTripOverviewEditTripModalOpened(
        editTripStepTypeToStatsigEvent[initialStep.stepType]
      );

      editTripStore.initialStep = { ...initialStep, isInitialStep: true };
      tripOverviewModalType.value = TripOverviewModalType.EditTrip;
    }

    function closeEditTripModal(): void {
      editTripStore.resetInitialStep();
      closeTripOverviewModal();
    }

    function openFerryFiltersModal(ingredientId: IngredientId): void {
      ferryFiltersStore.setAllowedFerryIngredientIds([ingredientId]);
      tripOverviewModalType.value = TripOverviewModalType.FerryFilters;
    }

    function closeFerryFiltersModal(): void {
      ferryFiltersStore.clearAllowedFerryIngredientIds();
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openFerryMultipleVehiclesModal(ingredientId: IngredientId): void {
      tripOverviewFerryMultipleVehiclesStore.setData(ingredientId);
      tripOverviewModalType.value = TripOverviewModalType.FerryMultipleVehicles;
    }

    function closeFerryMultipleVehiclesModal(): void {
      tripOverviewFerryMultipleVehiclesStore.clearData();
      closeTripOverviewModal();
    }

    function confirmFerryMultipleVehiclesModal(
      data: FerryVehiclesRouteData[]
    ): void {
      tripOverviewFerryMultipleVehiclesStore.update(data);
      closeTripOverviewModal();
    }

    function openPremadeTripDetailsModal(): void {
      tripOverviewModalType.value =
        TripOverviewModalType.PremadeTripDescription;
    }

    function openPortSelectionModal(
      currentIngredientId: string | undefined,
      fromPort: PortInfo | undefined,
      toPort: PortInfo | undefined,
      currentTransportType: TransportType.Flight | TransportType.Ferry,
      multipleVehicles: boolean
    ): void {
      portSelectorStore.setModalData(
        currentIngredientId,
        fromPort,
        toPort,
        currentTransportType,
        multipleVehicles
      );
      tripOverviewModalType.value = TripOverviewModalType.PortSelector;
    }

    function closePortSelectionModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function confirmPortSelectionModal(
      payload: PortSelectionUpdatePayload
    ): void {
      portSelectorStore.setPortsForTransport(payload);
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    async function awaitForCookieConsent(): Promise<void> {
      const delay = 500;
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          const cookieConsentKey = 'osano_consentmanager';
          const hasVisitedCookieConsentModal =
            !!localStorageService.readRawItem(cookieConsentKey);
          if (hasVisitedCookieConsentModal) {
            clearInterval(interval);
            resolve();
          }
        }, delay);
      });
    }

    async function openModalAutomatically(): Promise<void> {
      const panelParam = queryParamsStore.queryParams?.panel;
      const externalErrorParam = queryParamsStore.queryParams?.externalError;

      await awaitForCookieConsent();

      if (
        panelParam === ConfigurationPanelQueryParamValue.Calendar &&
        recipeStore.hotelOnlyRecipe.length > 0
      ) {
        if (browserStore.isSmallerThan900px) {
          tripOverviewPanelsStore.openConfigurationPanel(
            TabKey.Calendar,
            'auto'
          );
        } else {
          openTripCalendarModal();
        }
      } else {
        openInitialFerryModalWhenPossible();
      }

      if (externalErrorParam === ExternalErrorQueryParamValue.BookingFailed) {
        statsigEventsStore.logExternalErrorDisplayed(
          ExternalErrorQueryParamValue.BookingFailed
        );
        openExternalErrorModal();
      }

      isCookieModalOpen.value = false;
    }

    function openPriceGuaranteeModal(): void {
      statsigEventsStore.logTripOverviewPriceGuaranteeModalOpened();
      tripOverviewModalType.value = TripOverviewModalType.PriceGuarantee;
    }

    function openDestinationSelectorModal(
      searchText: string,
      ingredientId: IngredientId | undefined,
      modalType: DestinationAutocompleteType
    ): void {
      destinationSelectorStore.setDestinationSelectorData(
        searchText,
        ingredientId,
        modalType
      );
      tripOverviewModalType.value = TripOverviewModalType.DestinationSelector;
    }

    function selectUserPosition(
      autocompleteType: DestinationAutocompleteType,
      placeId?: string
    ): void {
      if (autocompleteType === AutoCompleteType.StartingPosition) {
        userLocationStore.setUserStartLocation(placeId);
      }

      if (autocompleteType === AutoCompleteType.EndingPosition) {
        userLocationStore.setUserEndLocation(placeId);
      }
      destinationSelectorStore.clearDestinationSelectorData();
      closeTripOverviewModal();
    }

    function confirmDestinationSelectorModal(
      newDestination: AutoCompleteSuggestion | null | undefined
    ): void {
      if (newDestination) {
        if (
          isLocationSuggestion(newDestination) &&
          (destinationSelectorStore.autocompleteType ===
            AutoCompleteType.StartingPosition ||
            destinationSelectorStore.autocompleteType ===
              AutoCompleteType.EndingPosition)
        ) {
          selectUserPosition(
            destinationSelectorStore.autocompleteType,
            newDestination.placeId
          );
        } else {
          destinationSelectorStore.handleConfirmSelection(newDestination);
        }
      }
      closeTripOverviewModal();
    }

    function openRemoveDestinationModal(ingredientId: IngredientId): void {
      removeDestinationModalFor.value = ingredientId;
      tripOverviewModalType.value = TripOverviewModalType.RemoveDestination;
    }

    function confirmRemoveDestinaionModal(): void {
      if (!removeDestinationModalFor.value) {
        return;
      }

      statsigEventsStore.logTripOverviewRemoveDestination();
      recipeStore.deleteDestinationIngredient(removeDestinationModalFor.value);
      removeDestinationModalFor.value = undefined;
      closeTripOverviewModal();
    }

    function buildNewTrip(): void {
      recipeStore.updateRecipe([]);
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openAddFlightsModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.AddFlights;
    }

    function closeAddFlightsModal(): void {
      editingIngredientStore.doneEditingIngredient();
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openFlightDetailsModal(modalData: FlightDetailsModalData): void {
      flightDetailsModalData.value = modalData;
      tripOverviewModalType.value = TripOverviewModalType.FlightDetails;
    }

    function closeFlightDetailsModal(): void {
      flightDetailsModalData.value = undefined;
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openContinueWithSuggestedDateModal(
      dateRange: string,
      numberOfNights: number | undefined
    ): void {
      const nightsText = `${numberOfNights} ${t('planner.nights', numberOfNights || 1)}`;
      addWarningMessage({
        title: t('date_confirmation_modal.continue_with_date_selected'),
        message: t('date_confirmation_modal.our_suggestion', [
          dateRange,
          nightsText,
        ]),
        highlight: `${dateRange} (${nightsText})`,
      });
      tripOverviewModalType.value = TripOverviewModalType.ConfirmationWarning;
    }

    function confirmWarningModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
      clearWarningMessages();
      bookTripStore.navigateToCheckout();
    }

    function closeWarningModal(): void {
      tripOverviewPanelsStore.closeTripOverviewPanel();
      tripOverviewModalType.value = TripOverviewModalType.None;
      clearWarningMessages();
      tripOverviewPageStore.scrollToCalendar();
    }

    function openPageReloadModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.ReloadPage;
      statsigEventsStore.logTripOverviewReloadPageModalOpened();
      amountReloadPageModalOpen.value++;
      if (amountReloadPageModalOpen.value > 1) {
        statsigEventsStore.logTripOverviewReloadPageFailed();
      }
    }

    function openAccommodationUpsellModal(): void {
      accommodationUpsellModalOpenCounter.value++;
      tripOverviewModalType.value = TripOverviewModalType.AccommodationUpsell;
    }

    function openPaxSelectorModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.PaxSelector;
    }

    function confirmPaxSelectorModal(newPax: Pax): void {
      paxStore.updatePax(newPax, CoreEventSource.TripOverview);
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openHotelFiltersModal(ingredientId: IngredientId): void {
      const hotelIngredient = recipeStore.getHotelIngredient(ingredientId);
      editingIngredientStore.setStayEditingIngredient(hotelIngredient);
      filtersStore.setHotelIngredientForFiltering();
      tripOverviewModalType.value = TripOverviewModalType.HotelFilters;
    }

    function closeHotelFiltersModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function applyHotelFiltersModal(): void {
      const newHotelIngredient = filtersStore.getChangedHotelIngredient();
      if (newHotelIngredient && editingIngredientStore.editingStayIngredient) {
        editingIngredientStore.editingStayIngredient.filters =
          newHotelIngredient.filters;
      }
      filtersStore.clearEditedHotelIngredient();
      closeHotelFiltersModal();
    }

    function updateHotelFiltersModal(event: HotelFilterUpdate): void {
      filtersStore.updateHotelFilter(
        event,
        RemoveHotelSelectionFiltersOrigin.TripOverview
      );
    }

    function resetHotelFiltersModal(): void {
      filtersStore.resetHotelFilters();
    }

    function openFlightsFiltersModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.FlightsFilters;
    }

    function openSembonusModal(): void {
      statsigEventsStore.logTripOverviewSembonusModalOpened(
        userStore.isUserLoggedIn
      );
      tripOverviewModalType.value = TripOverviewModalType.Sembonus;
    }

    function openAirportDistanceWarningModal(
      distanceInKm: number,
      hotelName: string | undefined,
      airportCode: string,
      isArrival: boolean
    ): void {
      const message = isArrival
        ? t('planner.airport_distance_warning_message_arrival', {
            airport: airportCode,
            hotel: hotelName,
            distance: Math.round(distanceInKm),
          })
        : t('planner.airport_distance_warning_message_departure', {
            airport: airportCode,
            hotel: hotelName,
            distance: Math.round(distanceInKm),
          });

      addWarningMessage({
        title: t('planner.airport_distance_warning_title'),
        message,
        highlight: airportCode,
      });
      tripOverviewModalType.value = TripOverviewModalType.ConfirmationWarning;
    }

    function addWarningMessage(message: ConfirmationWarningMessage): void {
      confirmationWarningData.value.push(message);
    }

    function clearWarningMessages(): void {
      confirmationWarningData.value = [];
    }

    return {
      // State
      tripOverviewModalType,
      ferryDetailsModalData,
      flightDetailsModalData,
      accommodationUpsellModalOpenCounter,
      confirmationWarningData,

      // Getters
      isAnyModalOpen,

      // Actions
      addWarningMessage,
      clearWarningMessages,
      openEditTripModal,
      closeEditTripModal,
      closeTripOverviewModal,
      openTripPriceSummaryModal,
      closeTripPriceSummaryModal,
      openFerryDetailsModal,
      closeFerryDetailsModal,
      openTripCalendarModal,
      closeTripCalendarModal,
      openPremadeTripDetailsModal,
      openWhyWeSuggestModal,
      openPortSelectionModal,
      closePortSelectionModal,
      openFerryFiltersModal,
      closeFerryFiltersModal,
      openFerryMultipleVehiclesModal,
      closeFerryMultipleVehiclesModal,
      confirmFerryMultipleVehiclesModal,
      confirmPortSelectionModal,
      openModalAutomatically,
      openPriceGuaranteeModal,
      openDestinationSelectorModal,
      selectUserPosition,
      confirmDestinationSelectorModal,
      openRemoveDestinationModal,
      confirmRemoveDestinaionModal,
      openAddFlightsModal,
      closeAddFlightsModal,
      openFlightDetailsModal,
      closeFlightDetailsModal,
      openContinueWithSuggestedDateModal,
      buildNewTrip,
      openInitialFerryModalWhenPossible,
      openPageReloadModal,
      openAccommodationUpsellModal,
      openPaxSelectorModal,
      confirmPaxSelectorModal,
      openHotelFiltersModal,
      closeHotelFiltersModal,
      applyHotelFiltersModal,
      updateHotelFiltersModal,
      resetHotelFiltersModal,
      openFlightsFiltersModal,
      openSembonusModal,
      openAirportDistanceWarningModal,
      closeWarningModal,
      confirmWarningModal,
    };
  }
);
